import React, { useState } from 'react';
import { Icon } from '@iconify/react';
import { useNavigate } from 'react-router-dom';
import Papa from 'papaparse';
import { useSelectedAccount } from '../../contexts/SelectedAccountContext';
import { post as apiPost } from '../../services/api/apiClient';
import { BackButton, Button, LoadingButton, useToast } from '../../components/ui';

// Length options
const lengthOptions = [
  {
    id: 'short',
    name: '短文',
    description: '200-300字，適合快速閱讀的精煉內容',
    icon: 'mdi:text-short',
    color: '#3B82F6'
  },
  {
    id: 'long',
    name: '長文',
    description: '300-500字，深度內容與完整故事',
    icon: 'mdi:text-long',
    color: '#8B5CF6'
  }
];

// Style options
const styleOptions = [
  {
    id: 'story',
    name: '故事感',
    description: '顛覆 + 互動',
    color: '#87CEEB'
  },
  {
    id: 'value',
    name: '價值觀',
    description: '觀點 + 金句',
    color: '#EC4899'
  },
  {
    id: 'professional',
    name: '專業',
    description: '洞察 + 方法',
    color: '#F59E0B'
  }
];

// Library options
const libraries = [
  { id: 'auto', name: '🎲 隨機選擇', description: '系統自動選擇最適合的故事庫' },
  { id: '短故事', name: '📝 短故事庫', file: '短故事.csv', description: '各類短文範例' },
  { id: '創業者合併', name: '🚀 創業故事', file: '創業者合併.csv', description: '創業相關故事' },
  { id: '感情故事', name: '💕 感情故事', file: '感情故事.csv', description: '愛情與情感故事' },
  { id: '行銷合併', name: '📈 行銷故事', file: '行銷合併.csv', description: '行銷相關案例' },
  { id: '年紀故事', name: '🎂 年紀故事', file: '年紀故事.csv', description: '人生階段故事' }
];

// Short story theme options (for filtering short stories by theme)
const shortStoryThemes = [
  { value: 'random', label: '隨機主題' },
  { value: '職場', label: '職場與工作' },
  { value: '戀愛', label: '戀愛與感情' },
  { value: '生活', label: '日常生活' },
  { value: '成長', label: '個人成長' },
  { value: '學習', label: '學習與教育' },
  { value: '家庭', label: '家庭關係' },
  { value: '友情', label: '朋友與社交' },
  { value: '心理', label: '心理與情緒' },
  { value: '創業', label: '創業與事業' },
  { value: '健康', label: '健康與運動' }
];

// Short story prompt style options
const shortStoryStyles = [
  { value: 'random', label: '隨機風格' },
  { value: 'inspirational', label: '勵志' },
  { value: 'lifestyle', label: '生活' },
  { value: 'ultra-short', label: '超短故事' },
  { value: 'emotional', label: '情感' }
];

// Prompt templates 已移至 backend/data/prompts/system-prompts.json

function RandomPostPage() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const { selectedAccount } = useSelectedAccount();

  const [selectedLength, setSelectedLength] = useState('short');
  const [selectedStyle, setSelectedStyle] = useState('story');
  const [selectedLibrary, setSelectedLibrary] = useState('auto');
  const [selectedTheme, setSelectedTheme] = useState('random'); // Short story theme
  const [selectedShortStyle, setSelectedShortStyle] = useState('random'); // Short story prompt style
  const [generatedPost, setGeneratedPost] = useState(null);
  const [isGenerating, setIsGenerating] = useState(false);
  const [currentProjectId, setCurrentProjectId] = useState(null); // 儲存創建的 Project ID

  /**
   * 在句號後換行，保持文章結構清晰
   * 只在句號（。）後換行，不在逗號或字中間斷開
   */
  const processCommasToNewlines = (text) => {
    if (!text) return text;

    // 在句號後加換行（但不影響已有的換行）
    // 句號後如果不是換行，就加換行
    let processed = text
      .replace(/。」/g, '」\n') // 。」 → 」\n
      .replace(/。(?!\n)/g, '。\n') // 句號後換行
      .replace(/\n\n+/g, '\n\n') // 多個換行合併成兩個
      .trim();

    return processed;
  };

  // getPrompt 已移至 backend，使用 API 調用

  const generatePost = async () => {
    setIsGenerating(true);

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        throw new Error('未登入或登入已過期，請重新登入');
      }

      // 1. 先創建 Project
      const projectData = {
        name: `Random Post - ${new Date().toLocaleDateString('zh-TW')}`,
        type: 'random-post',
        template: 'random-post',
        contentType: 'text',
        currentStep: 1,
        generationStatus: 'generating',
        settings: {
          strategy: 'random',
          length: selectedLength,
          style: selectedLength === 'long' ? selectedStyle : selectedShortStyle,
          library: selectedLength === 'long' ? selectedLibrary : null,
          theme: selectedLength === 'short' ? selectedTheme : null,
          source: 'random-post'
        }
      };

      const createResponse = await fetch('/api/projects/create', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(projectData)
      });

      const createResult = await createResponse.json();

      if (!createResult.success) {
        throw new Error(createResult.error || '創建專案失敗');
      }

      const projectId = createResult.project.id;
      setCurrentProjectId(projectId);
      console.log('✅ Project created on Random Post:', projectId);

      let referenceText = '';
      let themeLabel = '';

      if (selectedLength === 'short') {
        // ===== 短文模式：載入參考短文 =====

        // 從後端 API 載入短文 CSV
        const csvResponse = await fetch('/api/templates/short-posts-csv', {
          headers: { 'Authorization': `Bearer ${token}` }
        });

        if (!csvResponse.ok) {
          throw new Error('短文資料庫載入失敗');
        }

        const csvText = await csvResponse.text();

        // 解析 CSV 並根據主題過濾
        referenceText = await new Promise((resolve, reject) => {
          Papa.parse(csvText, {
            header: true,
            encoding: 'utf-8',
            skipEmptyLines: true,
            complete: (results) => {
              if (!results.data || results.data.length === 0) {
                reject(new Error('短文資料庫中沒有數據'));
                return;
              }

              let filteredPosts = results.data;

              // 如果選擇了特定主題，過濾 CSV 資料
              if (selectedTheme !== 'random') {
                filteredPosts = results.data.filter(post => {
                  const source = post['來源'] || post['source'] || '';
                  return source.includes(selectedTheme);
                });

                // 如果過濾後沒有結果，使用全部資料
                if (filteredPosts.length === 0) {
                  console.log(`主題 "${selectedTheme}" 沒有找到相關短文，使用全部短文`);
                  filteredPosts = results.data;
                } else {
                  console.log(`找到 ${filteredPosts.length} 篇 "${selectedTheme}" 主題的短文`);
                }
              }

              // 從過濾後的結果中隨機選擇一篇
              const randomIndex = Math.floor(Math.random() * filteredPosts.length);
              const selectedPost = filteredPosts[randomIndex];
              const content = selectedPost['內容'] || selectedPost['content'] || '';
              const postTheme = selectedPost['來源'] || selectedPost['source'] || '未分類';
              console.log(`隨機抽取的短文 (主題: ${postTheme}):`, content.substring(0, 50) + '...');
              resolve(content);
            },
            error: reject
          });
        });

        themeLabel = shortStoryThemes.find(t => t.value === selectedTheme)?.label || '隨機主題';

        // 調用後端 API 生成短文（使用安全模式）
        const data = await apiPost('/ai/generate-post', {
          promptType: 'randomPost',
          promptSubtype: 'short',
          promptStyle: selectedShortStyle || 'random',
          referenceText: referenceText,
          userInput: themeLabel,
          maxTokens: 500,
          temperature: 0.7
        });

        let generatedContent = data.content || data.text || '';

        // 後處理
        generatedContent = generatedContent
          .replace(/\*\*/g, '')
          .replace(/^#+\s*/gm, '')
          .replace(/^[\u300a\u3010\u3011\u300b\【\】].+[\u300a\u3010\u3011\u300b\【\】][:：]\s*/gm, '')
          .replace(/^(吸引人的開場|開場|標題|結尾|中段|takeaway)[:：]\s*/gim, '')
          .trim();

        generatedContent = processCommasToNewlines(generatedContent);

        // 更新 Project
        await fetch(`/api/projects/${projectId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            generatedImages: [{
              title: '',
              description: generatedContent,
              isFirstPage: true
            }],
            currentStep: 4,
            generationStatus: 'pending'
          })
        });

        setGeneratedPost({ content: generatedContent });
        toast.success('文章生成成功！');

      } else {
        // ===== 長文模式 =====

        // 選擇要使用的故事庫
        let libraryToUse = selectedLibrary;
        if (selectedLibrary === 'auto') {
          const longLibraries = ['創業者合併', '感情故事', '行銷合併', '年紀故事'];
          libraryToUse = longLibraries[Math.floor(Math.random() * longLibraries.length)];
        }

        const library = libraries.find(lib => lib.id === libraryToUse);

        // 從 Backend API 載入 CSV
        const csvResponse = await fetch(`/api/templates/long-story-csv?file=${encodeURIComponent(library.file)}`);
        if (!csvResponse.ok) {
          throw new Error('無法載入故事庫');
        }

        const csvText = await csvResponse.text();

        // 解析 CSV 並隨機選取
        const storyContent = await new Promise((resolve, reject) => {
          Papa.parse(csvText, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
              if (!results.data || results.data.length === 0) {
                reject(new Error('故事庫中沒有數據'));
                return;
              }

              const headers = results.meta.fields || [];
              const contentField = headers.find(h =>
                h.includes('內容') || h.includes('貼文內容') || h === 'content'
              ) || headers[0];

              const validStories = results.data.filter(row => {
                const content = row[contentField];
                if (!content || !content.trim()) return false;
                const trimmed = content.trim();
                if (trimmed.length < 10) return false;
                if (/^\d{4}[\/-]\d{1,2}[\/-]\d{1,2}/.test(trimmed)) return false;
                return true;
              });

              if (validStories.length === 0) {
                reject(new Error('故事庫中沒有有效的故事'));
                return;
              }

              const randomIndex = Math.floor(Math.random() * validStories.length);
              const selectedStory = validStories[randomIndex];
              resolve(selectedStory[contentField] || '');
            },
            error: reject
          });
        });

        // 構建人設
        const audience = selectedAccount?.settings?.audience || '';
        const topic = selectedAccount?.settings?.topic || '';
        const contentTask = selectedAccount?.settings?.content_task || '';
        const personaSetting = `受眾：${audience}\n主題：${topic}\n內容任務：${contentTask}`;

        // 調用後端 API 生成長文（使用安全模式）
        const data = await apiPost('/ai/generate-post', {
          promptType: 'randomPost',
          promptSubtype: 'long',
          promptStyle: selectedStyle || 'story',
          referenceText: storyContent,
          personaSetting: personaSetting,
          maxTokens: 800,
          temperature: 0.7
        });

        let generatedContent = data.content || data.text || '';

        // 後處理
        generatedContent = generatedContent
          .replace(/\*\*/g, '')
          .replace(/^#+\s*/gm, '')
          .replace(/^[\u300a\u3010\u3011\u300b\【\】].+[\u300a\u3010\u3011\u300b\【\】][:：]\s*/gm, '')
          .replace(/^(吸引人的開場|開場|標題|結尾|中段|takeaway)[:：]\s*/gim, '')
          .trim();

        generatedContent = processCommasToNewlines(generatedContent);

        // 更新 Project
        await fetch(`/api/projects/${projectId}`, {
          method: 'PUT',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            generatedImages: [{
              title: '',
              description: generatedContent,
              isFirstPage: true
            }],
            currentStep: 4,
            generationStatus: 'pending'
          })
        });

        setGeneratedPost({ content: generatedContent });
        toast.success('文章生成成功！');
      }

    } catch (error) {
      console.error('生成失敗:', error);
      toast.error(error.message || '生成失敗，請稍後再試');
    } finally {
      setIsGenerating(false);
    }
  };

  const handleCopy = () => {
    if (generatedPost?.content) {
      navigator.clipboard.writeText(generatedPost.content);
      toast.success('已複製到剪貼簿');
    }
  };

  const handleRegenerate = () => {
    setGeneratedPost(null);
    generatePost();
  };

  const handlePublish = async () => {
    if (!generatedPost?.content) return;

    // 確認已有 Project（由 generatePost 時創建）
    if (!currentProjectId) {
      toast.error('專案未找到，請重新生成');
      return;
    }

    try {
      const token = localStorage.getItem('token');
      if (!token) {
        toast.error('請先登入');
        navigate('/login');
        return;
      }

      // 更新 Project 的生成內容（如果用戶有編輯）
      await fetch(`/api/projects/${currentProjectId}`, {
        method: 'PUT',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          generatedImages: [{
            title: '',
            description: generatedPost.content,
            isFirstPage: true
          }]
        })
      });

      // 使用 URL 參數導航到 Step 4
      navigate(`/templates/content-generator/step4?projectId=${currentProjectId}`);

    } catch (error) {
      console.error('發布失敗:', error);
      toast.error(error.message || '發布失敗，請稍後再試');
    }
  };

  return (
    <div className="min-h-screen bg-white flex items-center justify-center p-8">
      {/* Back Button */}
      <BackButton
        onClick={() => navigate('/projects')}
        className="fixed top-8 left-8 z-[100]"
      >
        Back
      </BackButton>

      {/* Main Content Box */}
      <div className="max-w-[980px] w-full bg-white p-12">
        {/* Header */}
        <div className="text-center mb-12">
          <h1 className="text-4xl font-bold text-black mb-2">Random Post</h1>
          <p className="text-base text-gray-500">一鍵從故事庫隨機生成文章</p>
        </div>

        {/* Step 1: Length Selection */}
        <div className="mb-10">
          <div className="text-sm font-semibold text-slate-800 mb-4">1. 選擇文章長度</div>
          <div className="grid grid-cols-2 gap-4 mb-8">
            {lengthOptions.map((option) => {
              const isSelected = selectedLength === option.id;
              return (
                <div
                  key={option.id}
                  onClick={() => setSelectedLength(option.id)}
                  className={`p-6 rounded-xl cursor-pointer transition-all duration-200 flex items-start gap-4 hover:-translate-y-0.5 ${
                    isSelected ? 'border-2' : 'border border-gray-200 bg-slate-50'
                  }`}
                  style={{
                    borderColor: isSelected ? option.color : undefined,
                    background: isSelected ? `${option.color}08` : undefined
                  }}
                >
                  <div
                    className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                    style={{ background: isSelected ? option.color : '#e5e7eb' }}
                  >
                    <Icon
                      icon={option.icon}
                      className={`text-2xl ${isSelected ? 'text-white' : 'text-slate-500'}`}
                    />
                  </div>
                  <div className="flex-1">
                    <div
                      className="text-base font-semibold mb-1"
                      style={{ color: isSelected ? option.color : '#1e293b' }}
                    >
                      {option.name}
                    </div>
                    <div className="text-xs text-slate-500 leading-relaxed">
                      {option.description}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Step 2: Style Selection (for long form) */}
        {selectedLength === 'long' && (
          <div className="mb-10">
            <div className="text-sm font-semibold text-slate-800 mb-4">2. 選擇寫作風格</div>
            <div className="grid grid-cols-3 gap-4 mb-8">
              {styleOptions.map((style) => {
                const isSelected = selectedStyle === style.id;
                return (
                  <div
                    key={style.id}
                    onClick={() => setSelectedStyle(style.id)}
                    className={`p-5 rounded-lg cursor-pointer transition-all duration-200 text-center hover:-translate-y-0.5 ${
                      isSelected ? 'border-2' : 'border border-gray-200 bg-slate-50'
                    }`}
                    style={{
                      borderColor: isSelected ? style.color : undefined,
                      background: isSelected ? `${style.color}10` : undefined
                    }}
                  >
                    <div
                      className="text-sm font-semibold mb-1"
                      style={{ color: isSelected ? style.color : '#1e293b' }}
                    >
                      {style.name}
                    </div>
                    <div className="text-[10px] text-slate-500">
                      {style.description}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {/* Step 2 for Short: Theme Selection */}
        {selectedLength === 'short' && (
          <div className="mb-10">
            <div className="text-sm font-semibold text-slate-800 mb-4">2. 選擇故事主題</div>
            <div className="mb-8">
              <select
                value={selectedTheme}
                onChange={(e) => setSelectedTheme(e.target.value)}
                className="w-full p-4 border border-gray-200 rounded-lg text-sm text-slate-800 bg-white cursor-pointer"
              >
                {shortStoryThemes.map((theme) => (
                  <option key={theme.value} value={theme.value}>
                    {theme.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 3 for Short: Style Selection */}
        {selectedLength === 'short' && (
          <div className="mb-10">
            <div className="text-sm font-semibold text-slate-800 mb-4">3. 選擇語氣風格</div>
            <div className="mb-8">
              <select
                value={selectedShortStyle}
                onChange={(e) => setSelectedShortStyle(e.target.value)}
                className="w-full p-4 border border-gray-200 rounded-lg text-sm text-slate-800 bg-white cursor-pointer"
              >
                {shortStoryStyles.map((style) => (
                  <option key={style.value} value={style.value}>
                    {style.label}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Step 3 for Long: Library Selection */}
        {selectedLength === 'long' && (
          <div className="mb-10">
            <div className="text-sm font-semibold text-slate-800 mb-4">3. 選擇故事庫</div>
            <div className="mb-8">
              <select
                value={selectedLibrary}
                onChange={(e) => setSelectedLibrary(e.target.value)}
                className="w-full p-4 border border-gray-200 rounded-lg text-sm text-slate-800 bg-white cursor-pointer"
              >
                {libraries.map((lib) => (
                  <option key={lib.id} value={lib.id}>
                    {lib.name} - {lib.description}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Generate Button */}
        <LoadingButton
          onClick={generatePost}
          loading={isGenerating}
          loadingText="生成中..."
          icon="mdi:dice-multiple"
          variant="primary"
          size="lg"
          className="w-full"
        >
          隨機產文
        </LoadingButton>

        {/* Generated Result */}
        {generatedPost && (
          <div className="mt-12 p-8 bg-slate-50 rounded-xl border border-gray-200">
            <div className="flex items-center justify-between mb-4">
              <div className="text-base font-semibold text-slate-800 flex items-center gap-2">
                <Icon icon="mdi:check-circle" className="text-green-500" />
                生成結果
              </div>
              <div className="text-xs text-slate-400">
                {generatedPost.content.length} 字
              </div>
            </div>
            <textarea
              ref={(el) => {
                if (el) {
                  el.style.height = 'auto';
                  el.style.height = el.scrollHeight + 'px';
                }
              }}
              value={generatedPost.content}
              onChange={(e) => {
                setGeneratedPost({ ...generatedPost, content: e.target.value });
                // Auto-resize on content change
                e.target.style.height = 'auto';
                e.target.style.height = e.target.scrollHeight + 'px';
              }}
              className="w-full min-h-[150px] p-4 text-base text-slate-700 leading-relaxed border border-gray-200 rounded-lg resize-none font-inherit bg-white overflow-hidden"
            />
            <div className="flex gap-4 mt-6">
              <Button
                onClick={handleCopy}
                variant="secondary"
                size="md"
                icon="mdi:content-copy"
                className="flex-1"
              >
                複製
              </Button>
              <Button
                onClick={handleRegenerate}
                variant="secondary"
                size="md"
                icon="mdi:refresh"
                className="flex-1"
              >
                重新生成
              </Button>
              <Button
                onClick={handlePublish}
                variant="accent"
                size="md"
                icon="mdi:send"
                className="flex-[1.5]"
              >
                發布到 Threads
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export default RandomPostPage;
